import {
  HomeIcon,
  SettingsIcon,
  FolderIcon,
  MenuHorizontalIcon,
  DeliveryFilledIcon,
  ProductIcon,
  PersonIcon,
  IncomingIcon,
  PackageIcon,
  AtmWithdrawalIcon,
  StoreManagedIcon,
  LockFilledIcon,
  MobileIcon,
  BookOpenIcon,
  MoneyIcon,
} from '@shopify/polaris-icons';
import { Navigation, Text, BlockStack } from '@shopify/polaris';
import { useLocation } from 'react-router-dom';
import { default as packageInformation } from '../../package.json';
import helpers from 'helpers/index';
import Capacity from 'components/Capacity';
import { useAuth } from 'context/AuthContext';
import __helpers from 'helpers/index';

const generalMenu: any[] = [
  {
    url: '/',
    label: 'Trang chủ',
    icon: HomeIcon,
    exactMatch: true,
    permissions: [], // Quyền cần để truy cập mục này
  },
  {
    url: '/ticket_managers',
    label: 'Điều chuyến',
    icon: StoreManagedIcon,
    permissions: ['manage_trip_manifest'], // Quyền cần để truy cập mục này
  },

  {
    url: '/transactions',
    label: 'Giao dịch',
    icon: MoneyIcon,
    permissions: ['manage_orders'], // Quyền cần để truy cập mục này
  },
];

const tools = [
  {
    url: '/vehicle',
    label: 'Phương tiện',
    icon: DeliveryFilledIcon,
    permissions: ['manage_vehicle'], // Quyền cần để truy cập mục này
  },
  {
    url: '/seating',
    label: 'Số ghế & loại hình',
    icon: AtmWithdrawalIcon,
    permissions: ['manage_seating'], // Quyền cần để truy cập mục này
  },
  {
    url: '/journey',
    label: 'Hành trình',
    icon: StoreManagedIcon,
    permissions: ['manage_journey'], // Quyền cần để truy cập mục này
  },
  {
    url: '/orders',
    label: 'Danh sách đơn hàng',
    icon: ProductIcon,
    permissions: ['manage_orders'], // Quyền cần để truy cập mục này
  },
];

const saleMenus = [
  {
    url: '/customer',
    label: 'Khách hàng',
    icon: PersonIcon,
    permissions: ['manage_customers', 'view_customers', 'edit_customers'], // Quyền cần để truy cập mục này
  },
];

const advanceUserItems = [
  {
    url: '/media',
    label: 'Media',
    icon: FolderIcon,
    permissions: ['manage_media'], // Quyền cần để truy cập mục này
  },
];

/**
 * For admin
 */
let adminMenuItems = [
  { url: '/incoming_webhook', label: 'Incoming Webhook', icon: IncomingIcon },

  { url: '/outgoing_webhook', label: 'Outgoing Webhook', icon: PackageIcon },
  {
    url: '/settings/',
    label: 'Cài đặt chung',
    icon: SettingsIcon,
    subNavigationItems: [
      { url: '/settings', icon: MenuHorizontalIcon, label: 'System' },
      { url: '/account_setting', icon: MenuHorizontalIcon, label: 'Tài khoản' },
      { url: '/backup', icon: MenuHorizontalIcon, label: 'Back up' },
      { url: '/embed', icon: MenuHorizontalIcon, label: 'Mã nhúng & kết nối' },
      { url: '/media_setting', icon: MenuHorizontalIcon, label: 'Media' },
      { url: '/keymap', icon: MenuHorizontalIcon, label: 'Keymap' },
      { url: '/server_info', icon: MenuHorizontalIcon, label: 'Server info' },
    ],
  },

  { url: '/security/', label: 'Bảo mật', icon: LockFilledIcon },
  {
    url: '/users/',
    label: 'Người dùng',
    icon: PersonIcon,
  },
];

/**
 * START ...
 * @returns
 */
export default function Sidebar() {
  const location = useLocation();
  const version = process.env.REACT_APP_VERSION;
  const polarisVersion = packageInformation['dependencies']['@shopify/polaris'] || '';
  const { user: current_user_data } = useAuth();
  const { capacities, user_role } = current_user_data;

  /** Kiểm tra nếu người dùng hiện tại có quyền */
  const isAllowed = (current_user_can: string): boolean => {
    if (current_user_can === '') return true; // khi không yêu cầu quyền gì ...
    switch (user_role) {
      case 'super_admin':
        return true;
      case 'admin':
        return true;

      default:
        if (user_role === current_user_can) {
          return true;
        } else {
          if (__helpers.isObject(capacities))
            for (let key in capacities) {
              let value = capacities[key];
              if (Array.isArray(value) && value.includes(current_user_can)) {
                return true;
              }
            }
        }
        break;
    }
  };

  /**
   * capacities có dữ liệu dạng
   {
    tên nhóm quyền : [các quyền]
   },...
   */
  const filterMenusByPermission = (menus: any[]) => {
    return menus
      .map((menu) => {
        const hasPermission = helpers.isEmpty(menu.permissions) ? true : menu.permissions.some((permission: any) => isAllowed(permission));
        if (!hasPermission) return null;

        // Lọc subNavigationItems nếu có
        const subNavigationItems = menu.subNavigationItems?.filter((subMenu: any) =>
          helpers.isEmpty(subMenu.permissions) ? true : subMenu.permissions.some((permission: any) => isAllowed(permission))
        );

        return {
          ...menu,
          subNavigationItems, // Gán lại subNavigationItems sau khi lọc
        };
      })
      .filter(Boolean); // Loại bỏ các phần tử null
  };

  // Sử dụng hàm
  const _check_permission_generalMenu = filterMenusByPermission(generalMenu);
  const _check_permission_saleMenus = filterMenusByPermission(saleMenus);
  const _check_permission_toolsMenus = filterMenusByPermission(tools);
  const _check_permission_advanceUserItems = filterMenusByPermission(advanceUserItems);

  return (
    <Navigation location={location.pathname}>
      <Navigation.Section items={_check_permission_generalMenu} />
      {!helpers.isEmpty(_check_permission_toolsMenus) && <Navigation.Section title="Quản trị" separator items={_check_permission_toolsMenus} />}
      {!helpers.isEmpty(_check_permission_saleMenus) && <Navigation.Section title="Dành cho Sale" separator items={_check_permission_saleMenus} />}

      {!helpers.isEmpty(_check_permission_advanceUserItems) && (
        <Navigation.Section title="Nâng cao" separator items={_check_permission_advanceUserItems} />
      )}

      <Capacity current_user_can="admin">
        <Navigation.Section title="Quản trị CRM" separator items={adminMenuItems} />
      </Capacity>

      <Navigation.Section fill items={[]} />

      <BlockStack align="center" inlineAlign="center" gap={'200'}>
        <Text as="p" variant="bodyXs" tone="subdued">
          NBEE CRM &copy;2021, version {version}
          <br />
          build: {packageInformation.build_version}
          <br />
          {helpers.isDev() && `Polaris Framework ver ${polarisVersion}`}
        </Text>
        <div style={{ marginBottom: 5, marginTop: 5 }}></div>
      </BlockStack>
    </Navigation>
  );
}

